import { useState } from "react";
import { Icon } from "#/components/Icon.tsx";
import { format } from "#/i18n/format.tsx";
import type { Messages } from "#/i18n/index.ts";
import { THEME_COOKIE, nextTheme, type Theme } from "#/lib/theme.ts";

const ICONS = { system: "monitor", light: "sun", dark: "moon" } as const;

/** One year, in seconds: how long an explicit theme choice is remembered. */
const ONE_YEAR = 31_536_000;

/**
 * Cycles the colour theme through system, light and dark.
 *
 * The choice is written to a first-party cookie so the server renders the same
 * theme on the next request; `system` deletes the cookie and follows the
 * operating system again.
 *
 * @param props - The theme the page was rendered with, and the copy.
 * @returns The theme button.
 */
export function ThemeToggle({ initial, messages }: { initial: Theme; messages: Messages }) {
  const [theme, setTheme] = useState(initial);
  const next = nextTheme(theme);
  const label = format(messages.theme.label, { current: messages.theme[theme], next: messages.theme[next] });
  return (
    <button
      type="button"
      className="icon-button"
      aria-label={label}
      title={label}
      onClick={() => {
        const maxAge = next === "system" ? 0 : ONE_YEAR;
        document.cookie = `${THEME_COOKIE}=${next}; Path=/; Max-Age=${String(maxAge)}; SameSite=Lax`;
        if (next === "system") delete document.documentElement.dataset.theme;
        else document.documentElement.dataset.theme = next;
        setTheme(next);
      }}
    >
      <Icon name={ICONS[theme]} />
    </button>
  );
}
